import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { Bar, Line } from 'react-chartjs-2';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const IncomeExpenseSummary: React.FC = () => {
    const [incomeData, setIncomeData] = useState<{ [key: string]: { total_income: string; total_expenses: string; total_salaries: string; net_income: string } }>({});
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let cookie of cookieArray) {
            cookie = cookie.trim();
            if (cookie.indexOf(cookieName) === 0) return cookie.substring(cookieName.length);
        }
        return '';
    };

    const fetchIncomeData = async (year: number) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/fees/income/?year=${year}`);
            setIncomeData(response.data.income_and_expenses_by_month || {});
        } catch (error) {
            console.error('Error fetching income data:', error);
            setIncomeData({});
            setError('Failed to fetch income data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (direction: 'previous' | 'next') => {
        setCurrentYear(prevYear => {
            const newYear = direction === 'previous' ? prevYear - 1 : prevYear + 1;
            fetchIncomeData(newYear);
            return newYear;
        });
    };

    const downloadCSV = () => {
        const headers = ['Month', 'Total Income', 'Total Expenses', 'Total Salaries', 'Net Income'];
        const rows = months.map(month => {
            const data = incomeData[month];
            return [
                month,
                formatCurrency(parseFloat(data.total_income?.replace(/,/g, '') || '0')),
                formatCurrency(parseFloat(data.total_expenses?.replace(/,/g, '') || '0')),
                formatCurrency(parseFloat(data.total_salaries?.replace(/,/g, '') || '0')),
                formatCurrency(parseFloat(data.net_income?.replace(/,/g, '') || '0')),
            ];
        });
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(',')),
            [
                'Yearly Total',
                formatCurrency(yearlyTotalIncome),
                formatCurrency(yearlyTotalExpenses),
                formatCurrency(yearlyTotalSalaries),
                formatCurrency(yearlyNetIncome),
            ].join(','),
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Income_Expense_Summary_${currentYear}.csv`;
        link.click();
    };

    useEffect(() => {
        const checkAuth = async () => {
            const token = getCookie('token');
            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                dispatch(setAuth(true));
            } else {
                dispatch(setAuth(false));
            }
        };
        checkAuth();
    }, [dispatch]);

    useEffect(() => {
        if (auth) {
            fetchIncomeData(currentYear);
        }
    }, [auth, currentYear]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value).replace('KES', 'KSh');
    };

    const monthOrder = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const months = Object.keys(incomeData).sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));

    let yearlyTotalIncome = 0, yearlyTotalExpenses = 0, yearlyTotalSalaries = 0, yearlyNetIncome = 0;

    months.forEach(month => {
        const data = incomeData[month];
        yearlyTotalIncome += parseFloat(data.total_income?.replace(/,/g, '') || '0');
        yearlyTotalExpenses += parseFloat(data.total_expenses?.replace(/,/g, '') || '0');
        yearlyTotalSalaries += parseFloat(data.total_salaries?.replace(/,/g, '') || '0');
        yearlyNetIncome += parseFloat(data.net_income?.replace(/,/g, '') || '0');
    });

    const chartData = {
        labels: months,
        datasets: [
            {
                label: 'Total Income',
                data: months.map(month => parseFloat(incomeData[month].total_income?.replace(/,/g, '') || '0')),
                backgroundColor: 'rgba(34, 197, 94, 0.6)', // Green
                borderColor: 'rgba(34, 197, 94, 1)',
                borderWidth: 1,
                tension: chartType === 'line' ? 0.4 : 0,
                fill: chartType === 'line' ? false : true,
            },
            {
                label: 'Total Expenses',
                data: months.map(month => parseFloat(incomeData[month].total_expenses?.replace(/,/g, '') || '0')),
                backgroundColor: 'rgba(249, 115, 22, 0.6)', // Orange
                borderColor: 'rgba(249, 115, 22, 1)',
                borderWidth: 1,
                tension: chartType === 'line' ? 0.4 : 0,
                fill: chartType === 'line' ? false : true,
            },
            {
                label: 'Total Salaries',
                data: months.map(month => parseFloat(incomeData[month].total_salaries?.replace(/,/g, '') || '0')),
                backgroundColor: 'rgba(168, 85, 247, 0.6)', // Purple
                borderColor: 'rgba(168, 85, 247, 1)',
                borderWidth: 1,
                tension: chartType === 'line' ? 0.4 : 0,
                fill: chartType === 'line' ? false : true,
            },
            {
                label: 'Net Income',
                data: months.map(month => parseFloat(incomeData[month].net_income?.replace(/,/g, '') || '0')),
                backgroundColor: months.map(month => parseFloat(incomeData[month].net_income?.replace(/,/g, '') || '0') >= 0 ? 'rgba(59, 130, 246, 0.6)' : 'rgba(239, 68, 68, 0.6)'), // Blue or Red
                borderColor: months.map(month => parseFloat(incomeData[month].net_income?.replace(/,/g, '') || '0') >= 0 ? 'rgba(59, 130, 246, 1)' : 'rgba(239, 68, 68, 1)'),
                borderWidth: 1,
                tension: chartType === 'line' ? 0.4 : 0,
                fill: chartType === 'line' ? false : true,
            },
        ],
    };

    const chartOptions: ChartOptions<'bar' | 'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: { size: 14 },
                    color: '#1e293b',
                },
            },
            title: {
                display: true,
                text: `Financial Overview for ${currentYear}`,
                font: { size: 18, weight: 'bold' },
                color: '#1e293b',
                padding: { top: 10, bottom: 20 },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: ${formatCurrency(value)}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (tickValue: string | number) {
                        return formatCurrency(Number(tickValue));
                    },
                    color: '#1e293b',
                },
                grid: { color: 'rgba(0, 0, 0, 0.1)' },
            },
            x: {
                ticks: { color: '#1e293b' },
                grid: { display: false },
            },
        },
    };

    if (!auth) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
                <div className="text-center p-4 bg-white rounded shadow">
                    <i className="fas fa-exclamation-circle text-danger fa-3x mb-3"></i>
                    <h4 className="text-danger mb-2">Unauthorized Access</h4>
                    <p className="text-muted">Please login to access this page</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container bg-light min-vh-100">
            <div className="container-fluid">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
                    <div className="mb-3 mb-md-0">
                        <h2 className="fw-bold text-dark">Income & Expense Summary</h2>
                        <p className="text-muted">Financial overview for {currentYear}</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <button
                            className="btn btn-primary d-flex align-items-center gap-2"
                            onClick={() => handleYearChange('previous')}
                            aria-label="Previous Year"
                        >
                            <i className="fas fa-chevron-left"></i> Previous
                        </button>
                        <span className="fw-bold text-dark align-self-center">{currentYear}</span>
                        <button
                            className="btn btn-primary d-flex align-items-center gap-2"
                            onClick={() => handleYearChange('next')}
                            aria-label="Next Year"
                        >
                            Next <i className="fas fa-chevron-right"></i>
                        </button>
                        <button
                            className="btn btn-success d-flex align-items-center gap-2"
                            onClick={downloadCSV}
                            aria-label="Download CSV"
                        >
                            <i className="fas fa-download"></i> CSV
                        </button>
                        <button
                            className="btn btn-secondary d-flex align-items-center gap-2"
                            onClick={() => setChartType(chartType === 'bar' ? 'line' : 'bar')}
                            aria-label={`Switch to ${chartType === 'bar' ? 'Line' : 'Bar'} Chart`}
                        >
                            <i className={`fas fa-${chartType === 'bar' ? 'chart-line' : 'chart-bar'}`}></i>
                            {chartType === 'bar' ? 'Line' : 'Bar'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                ) : months.length > 0 ? (
                    <>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-6 col-lg-3">
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-body d-flex align-items-center gap-3">
                                        <i className="fas fa-wallet text-success fa-2x"></i>
                                        <div>
                                            <h6 className="text-muted mb-1">Total Income</h6>
                                            <p className="fw-bold text-dark mb-0">{formatCurrency(yearlyTotalIncome)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-3">
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-body d-flex align-items-center gap-3">
                                        <i className="fas fa-receipt text-warning fa-2x"></i>
                                        <div>
                                            <h6 className="text-muted mb-1">Total Expenses</h6>
                                            <p className="fw-bold text-dark mb-0">{formatCurrency(yearlyTotalExpenses)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-3">
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-body d-flex align-items-center gap-3">
                                        <i className="fas fa-user-tie text-purple fa-2x"></i>
                                        <div>
                                            <h6 className="text-muted mb-1">Total Salaries</h6>
                                            <p className="fw-bold text-dark mb-0">{formatCurrency(yearlyTotalSalaries)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-3">
                                <div className="card shadow-sm border-0 h-100">
                                    <div className="card-body d-flex align-items-center gap-3">
                                        <i className={`fas fa-chart-line ${yearlyNetIncome >= 0 ? 'text-primary' : 'text-danger'} fa-2x`}></i>
                                        <div>
                                            <h6 className="text-muted mb-1">Net Income</h6>
                                            <p className={`fw-bold ${yearlyNetIncome >= 0 ? 'text-primary' : 'text-danger'} mb-0`}>
                                                {formatCurrency(yearlyNetIncome)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-lg-6">
                                <div className="card shadow-sm border-0">
                                    <div className="card-body p-0">
                                        <div className="table" style={{ maxHeight: '200px' }}>
                                            <table className="table table-hover table-striped mb-0">
                                                <thead className="bg-light sticky-top">
                                                    <tr>
                                                        <th className="p-3">Month</th>
                                                        <th className="p-3">Total Income</th>
                                                        <th className="p-3">Total Expenses</th>
                                                        <th className="p-3">Total Salaries</th>
                                                        <th className="p-3">Net Income</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {months.map((month, index) => {
                                                        const data = incomeData[month];
                                                        const income = parseFloat(data.total_income?.replace(/,/g, '') || '0');
                                                        const expenses = parseFloat(data.total_expenses?.replace(/,/g, '') || '0');
                                                        const salaries = parseFloat(data.total_salaries?.replace(/,/g, '') || '0');
                                                        const net = parseFloat(data.net_income?.replace(/,/g, '') || '0');
                                                        return (
                                                            <tr key={month}>
                                                                <td className="p-3">{month}</td>
                                                                <td className="p-3">{formatCurrency(income)}</td>
                                                                <td className="p-3">{formatCurrency(expenses)}</td>
                                                                <td className="p-3">{formatCurrency(salaries)}</td>
                                                                <td className={`p-3 ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                    {formatCurrency(net)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-light fw-bold">
                                                        <td className="p-3">Yearly Total</td>
                                                        <td className="p-3">{formatCurrency(yearlyTotalIncome)}</td>
                                                        <td className="p-3">{formatCurrency(yearlyTotalExpenses)}</td>
                                                        <td className="p-3">{formatCurrency(yearlyTotalSalaries)}</td>
                                                        <td className={`p-3 ${yearlyNetIncome >= 0 ? 'text-success' : 'text-danger'}`}>
                                                            {formatCurrency(yearlyNetIncome)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-lg-6">
                                <div className="card shadow-sm border-0" style={{ height: '400px' }}>
                                    <div className="card-body p-4">
                                        {chartType === 'bar' ? (
                                            <Bar data={chartData} options={chartOptions} />
                                        ) : (
                                            <Line data={chartData} options={chartOptions} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="card shadow-sm border-0">
                        <div className="card-body text-center">
                            <i className="fas fa-info-circle text-primary fa-3x mb-3"></i>
                            <p className="text-muted fw-semibold">No financial data available for {currentYear}</p>
                        </div>
                    </div>
                )}
            </div>
            <style>{`
                .dashboard-container {
                    padding: 24px;
                    min-height: 100vh;
                    margin-left: 220px;
                    width: calc(100% - 220px);
                }
                @media (max-width: 980px) {
                    .dashboard-container {
                        margin-left: 0;
                        width: 100%;
                    }
                }
                .card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }
                .btn {
                    transition: all 0.2s ease;
                }
                .btn:hover {
                    transform: translateY(-1px);
                }
                .text-purple {
                    color: #6b46c1;
                }
            `}</style>
        </div>
    );
};

export default IncomeExpenseSummary;